import { claudeCodeInstance } from '../claudeCodeDashboard/instance';
import type {
  CatalogAgentView,
  CreateLoopInput,
  ListLoopsFilters,
  LoopListView,
  LoopView,
  TransitionLoopInput,
  UpdateLoopInput,
  CatalogView,
  CommandCenterSummaryView,
  CreateManagedHermesAgentInput,
  CreateManagedN8nAgentInput,
  FleetView,
  HeatmapView,
  ListRunsFilters,
  NodeRegistryView,
  RunDetailView,
  RunListPage,
  RunSummaryView,
  SkillContentView,
  SkillsView,
} from './types';

/** Live n8n workflows as catalog cards plus their recent executions as runs, fetched server-side. */
export interface N8nLiveView {
  agents: CatalogAgentView[];
  runs: RunSummaryView[];
  /** One day of executions per card and hour; absent from a board cached before it existed. */
  heatmap?: HeatmapView;
  /** The card window of executions, which a card totals into its run count. */
  cardHeatmap?: HeatmapView;
}

/** The board itself: sections and cards, live and planned, in one round trip. */
export const fetchCatalog = async (): Promise<CatalogView> => {
  const response = await claudeCodeInstance.get<CatalogView>('/agent/commandCenter/catalog');
  return response.data;
};

/** n8n workflows + executions fetched live; workflows feed the picker and runs join managed cards client-side. */
export const fetchN8nLive = async (): Promise<N8nLiveView> => {
  const response = await claudeCodeInstance.get<N8nLiveView>('/agent/commandCenter/n8n/live');
  return response.data;
};

/** Run density per agent and hour over the trailing window; the API defaults to 24h and caps at 168. */
export const fetchHeatmap = async (windowHours?: number): Promise<HeatmapView> => {
  const response = await claudeCodeInstance.get<HeatmapView>('/agent/commandCenter/heatmap', {
    params: { windowHours },
  });
  return response.data;
};

/** KPI tiles: fleet presence plus today's run counters. */
export const fetchCommandCenterSummary = async (): Promise<CommandCenterSummaryView> => {
  const response = await claudeCodeInstance.get<CommandCenterSummaryView>('/agent/commandCenter/summary');
  return response.data;
};

/** Every node ever registered, online ones first. */
export const fetchFleetNodes = async (): Promise<FleetView> => {
  const response = await claudeCodeInstance.get<FleetView>('/agent/commandCenter/nodes');
  return response.data;
};

/** Every enrolled node with its approval state, plus how many are still queued. */
export const fetchNodeRegistry = async (): Promise<NodeRegistryView> => {
  const response = await claudeCodeInstance.get<NodeRegistryView>('/agent/commandCenter/nodes/registry');
  return response.data;
};

/** Validates a Hermes catalog version and registers its live board card without assigning it to a node. */
export const createManagedHermesAgent = async (input: CreateManagedHermesAgentInput): Promise<CatalogAgentView> => {
  const response = await claudeCodeInstance.post<CatalogAgentView>('/agent/commandCenter/agents/hermes', input);
  return response.data;
};

/** Removes only the managed monitoring card; the Hermes skill, node state, and run history remain intact. */
export const deleteManagedHermesAgent = async (skillId: string): Promise<void> => {
  await claudeCodeInstance.delete(`/agent/commandCenter/agents/hermes/${encodeURIComponent(skillId)}`);
};

/** Validates a live n8n workflow and registers only its monitoring card. */
export const createManagedN8nAgent = async (input: CreateManagedN8nAgentInput): Promise<CatalogAgentView> => {
  const response = await claudeCodeInstance.post<CatalogAgentView>('/agent/commandCenter/agents/n8n', input);
  return response.data;
};

/** Removes only the monitoring card; the n8n workflow and execution history remain available. */
export const deleteManagedN8nAgent = async (workflowId: string): Promise<void> => {
  await claudeCodeInstance.delete(`/agent/commandCenter/agents/n8n/${encodeURIComponent(workflowId)}`);
};

/** Runs newest-first; every filter is optional and the API caps limit at 200. */
export const fetchRuns = async (filters: ListRunsFilters = {}): Promise<RunListPage> => {
  const response = await claudeCodeInstance.get<RunListPage>('/agent/commandCenter/runs', {
    params: {
      status: filters.status,
      nodeId: filters.nodeId?.trim() || undefined,
      agentId: filters.agentId,
      from: filters.from,
      to: filters.to,
      limit: filters.limit,
      offset: filters.offset,
    },
  });
  return response.data;
};

/** One run with its paged event timeline. */
export const fetchRunDetail = async (runId: string, limit?: number, offset?: number): Promise<RunDetailView> => {
  const response = await claudeCodeInstance.get<RunDetailView>(
    `/agent/commandCenter/runs/${encodeURIComponent(runId)}`,
    { params: { limit, offset } },
  );
  return response.data;
};

/** The skill catalog: team-created and admin-authored skills the control plane can push to nodes. */
export const fetchSkills = async (): Promise<SkillsView> => {
  const response = await claudeCodeInstance.get<SkillsView>('/agent/commandCenter/skills');
  return response.data;
};

/** The SKILL.md an agent wrote, so the board can show what a skill actually does. */
export const fetchSkillContent = async (skillId: string): Promise<SkillContentView> => {
  const response = await claudeCodeInstance.get<SkillContentView>(
    `/agent/commandCenter/skills/${encodeURIComponent(skillId)}/content`,
  );
  return response.data;
};

/** Files a loop from the board's planning form; the server derives its key from the name. */
export const createLoop = async (input: CreateLoopInput): Promise<LoopView> => {
  const response = await claudeCodeInstance.post<LoopView>('/agent/commandCenter/loops', input);
  return response.data;
};

/** The loop registry — what the team plans to build, alongside what already runs. */
export const fetchLoops = async (filters: ListLoopsFilters = {}): Promise<LoopListView> => {
  const response = await claudeCodeInstance.get<LoopListView>('/agent/commandCenter/loops', { params: filters });
  return response.data;
};

export const fetchLoop = async (loopId: string): Promise<LoopView> => {
  const response = await claudeCodeInstance.get<LoopView>(`/agent/commandCenter/loops/${loopId}`);
  return response.data;
};

/** Edits a loop's description. State is not editable here — that is what transitionLoop is for. */
export const updateLoop = async (loopId: string, patch: UpdateLoopInput): Promise<LoopView> => {
  const response = await claudeCodeInstance.patch<LoopView>(`/agent/commandCenter/loops/${loopId}`, patch);
  return response.data;
};

/** The only door a loop's state moves through. Illegal moves come back as 400, conflicts as 409. */
export const transitionLoop = async (loopId: string, input: TransitionLoopInput): Promise<LoopView> => {
  const response = await claudeCodeInstance.post<LoopView>(`/agent/commandCenter/loops/${loopId}/transition`, input);
  return response.data;
};

/** Removes a loop filed by mistake; the server refuses once it has left `idea`. */
export const deleteLoop = async (loopId: string): Promise<void> => {
  await claudeCodeInstance.delete(`/agent/commandCenter/loops/${loopId}`);
};
